// @ts-check
import { Box as DefaultBox } from '@material-ui/core';
import defaultAxios from 'axios';
import React, { useEffect, useState } from 'react';
import defaultConfig from '../../../App/config';
import { CONSTANTS } from '../../../utility';
import {isProduction} from '../../../../src/utility/information'

/** @type {React.CSSProperties} */
const STYLE_CONTAINER_MENU_PHOTO_NOT_PRESENT = {
  alignItems: 'center',
  border: '1px solid #dcdcdc',
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  width: '100%',
};

/** @type {React.CSSProperties} */
const STYLE_CONTAINER_MENU_PHOTO_PRESENT = {
  ...STYLE_CONTAINER_MENU_PHOTO_NOT_PRESENT,
  alignItems: 'flex-start',
  border: 'none',
  bottom: 16,
  color: CONSTANTS.COLOR_NORMAL_TEXT,
  position: 'relative',
  textDecoration: 'none',
};

/** @type {React.CSSProperties} */
const STYLE_HEADER_MENU_PHOTO_NOT_PRESENT = {
  color: CONSTANTS.COLOR_HEADER,
  fontSize: 20,
  fontWeight: 500,
};

/** @type {React.CSSProperties} */
const STYLE_LINK_WITH_MENU_PHOTO_URL = {
  color: CONSTANTS.COLOR_LINK,
};

/** @type {React.CSSProperties} */
const STYLE_IMG_WITH_MENU_PHOTO_URL = {
  backgroundColor: '#a7a7a7',
  flex: '0 0 100%',
  height: '100%',
  marginTop: 4,
  objectFit: 'contain',
  width: '100%',
};

/** @type {React.CSSProperties} */
const STYLE_IMG_MENU_PHOTO_NOT_PRESENT_PLACEHOLDER = { height: 200, width: 200 };

/** @type {React.CSSProperties} */
const STYLE_TEXT_ALONGSIDE_LINK_WITH_MENU_PHOTO_URL = {
  color: CONSTANTS.COLOR_NORMAL_TEXT,
};

/**
 * @typedef Props
 * @property {typeof DefaultBox} [Box]
 * @property {Pick<typeof defaultAxios, 'request'>} [axios]
 * @property {Pick<typeof defaultConfig, 'menuAttachedFilesApi'>} [config]
 * @property {string | undefined} menuPhoto
 * @param {import('@material-ui/core').BoxProps & Props} arg0
 */
const ProductDetailMenuPhoto = ({
  Box /* istanbul ignore next */ = DefaultBox,
  axios /* istanbul ignore next */ = defaultAxios,
  config /* istanbul ignore next */ = defaultConfig,
  menuPhoto,
  ...props
}) => {
  const [hasImageLoadFailed, setImageLoadHasFailed] = useState(false);
  const [isLoadingMenuPhotoURL, setIsLoadingMenuPhotoURL] = useState(false);
  const [menuPhotoURL, setMenuPhotoURL] = useState('');

  useEffect(() => {
    const fetchMenuPhotoURL = async () => {
      if (!!menuPhoto) {
        setIsLoadingMenuPhotoURL(true);

        const reTryMenuUrl = isProduction ? "https://apac-vss.deliveryhero.com/menuimages/api/v1/menuImages" : "https://apac-api.dev.pandaqc.io/menuimages/api/v1/menuImages"

        try {
          /** @type {AxiosResponse<{ url: string }>} */
          const {
            data: { url },
          } = await axios.request({
            baseURL: config.menuAttachedFilesApi,
            method: 'GET',
            url: `/entry/${menuPhoto}`,
          });

          setMenuPhotoURL(url);
        } catch {
          try {
            const {
              data: { url },
            } = await axios.request({
              baseURL: reTryMenuUrl,
              method: 'GET',
              url: `/entry/${menuPhoto}`,
            });
  
            setMenuPhotoURL(url);
          } 
          catch{}
        } finally {
          setIsLoadingMenuPhotoURL(false);
        }
      }
    };

    fetchMenuPhotoURL();
  }, [axios, config, menuPhoto]);

  /** @type {React.DOMAttributes<HTMLImageElement>['onError']} */
  const onImageLoadError = () => setImageLoadHasFailed(true);

  /** @type {React.ReactNode} */
  let children = null;

  if (hasImageLoadFailed && !!menuPhotoURL) {
    children = (
      <div style={STYLE_CONTAINER_MENU_PHOTO_NOT_PRESENT}>
        <img
          alt="Placeholder"
          src="/assets/NoSearchResults.svg"
          style={STYLE_IMG_MENU_PHOTO_NOT_PRESENT_PLACEHOLDER}
        />

        <span style={STYLE_HEADER_MENU_PHOTO_NOT_PRESENT}>The attached menu is in PDF format.</span>

        <Box mt={1}>
          <a
            href={menuPhotoURL}
            rel="noopener noreferrer"
            target="_blank"
            style={STYLE_LINK_WITH_MENU_PHOTO_URL}
          >
            Click to open the file
          </a>{' '}
          <span style={STYLE_TEXT_ALONGSIDE_LINK_WITH_MENU_PHOTO_URL}>in a new tab</span>
        </Box>
      </div>
    );
  } else if (!!menuPhotoURL) {
    children = (
      <a
        href={menuPhotoURL}
        rel="noopener noreferrer"
        target="_blank"
        style={STYLE_CONTAINER_MENU_PHOTO_PRESENT}
      >
        <span>Menu photo uploaded by the Vendor:</span>

        <img
          alt="Menu"
          onError={onImageLoadError}
          src={menuPhotoURL}
          style={STYLE_IMG_WITH_MENU_PHOTO_URL}
        />
      </a>
    );
  } else if (!isLoadingMenuPhotoURL) {
    children = (
      <div style={STYLE_CONTAINER_MENU_PHOTO_NOT_PRESENT}>
        <img
          alt="Placeholder"
          src="/assets/NoSearchResults.svg"
          style={STYLE_IMG_MENU_PHOTO_NOT_PRESENT_PLACEHOLDER}
        />

        <span style={STYLE_HEADER_MENU_PHOTO_NOT_PRESENT}>No menu photo available</span>
      </div>
    );
  }

  /**
   * Limit the height to take care of padding. Note that it's not 80px, but 64px, since we move the
   * children up a big.
   */
  return (
    <Box bgcolor="#f7f7f7" padding={5} {...props} display="block">
      {children}
    </Box>
  );
};

export default ProductDetailMenuPhoto;
